import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { PaymentService } from '../service/payment.service';

const ensureValidSession = (
  authService: AuthService,
  router: Router,
): Observable<boolean | UrlTree> => {
  if (!authService.isAuthenticated()) {
    return of(router.createUrlTree(['/login']));
  }

  if (authService.hasValidAccessToken()) {
    return of(true);
  }

  return authService.refresh().pipe(
    map(() => true),
    catchError(() => of(router.createUrlTree(['/login']))),
  );
};

const resolveAuthenticatedRedirect = (
  authService: AuthService,
  paymentService: PaymentService,
  router: Router,
): Observable<UrlTree> => {
  const status = paymentService.serviceStatus();

  const getAuthenticatedLanding = (): UrlTree => {
    if (authService.requiresContractSignature()) {
      return router.createUrlTree(['/contract-signature']);
    }

    return router.createUrlTree(['/home']);
  };

  if (status !== null) {
    return of(
      status.service_active
        ? getAuthenticatedLanding()
        : router.createUrlTree(['/subscription-inactive']),
    );
  }

  return paymentService.updateServiceStatus().pipe(
    map((result) =>
      result.service_active
        ? getAuthenticatedLanding()
        : router.createUrlTree(['/subscription-inactive']),
    ),
    catchError((error) => {
      console.error('Payment status check failed, redirecting to server error page:', error);
      return of(router.createUrlTree(['/server-error']));
    }),
  );
};

/**
 * Guard that allows access only to authenticated users.
 * Redirects to /login if the user is not authenticated.
 */
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return ensureValidSession(authService, router);
};

/**
 * Guard that allows access only to guest users (not authenticated).
 * Redirects to /home if the user is already authenticated.
 */
export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const paymentService = inject(PaymentService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return true;
  }

  if (authService.hasValidAccessToken()) {
    return resolveAuthenticatedRedirect(authService, paymentService, router);
  }

  return authService.refresh().pipe(
    switchMap(() => resolveAuthenticatedRedirect(authService, paymentService, router)),
    catchError(() => of(true)),
  );
};

/**
 * Guard that enforces the contract signature onboarding flow for non-admin users.
 * Non-admin users must complete signature before accessing protected pages.
 * Note: assumes authGuard has already verified authentication on the parent route.
 */
export const contractOnboardingGuard: CanActivateChildFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAdmin()) {
    return true;
  }

  const isContractRoute = state.url.startsWith('/contract-signature');

  if (authService.requiresContractSignature()) {
    return isContractRoute ? true : router.createUrlTree(['/contract-signature']);
  }

  return isContractRoute ? router.createUrlTree(['/home']) : true;
};
